import { useConfig } from "@jti/app-context/useConfig";
import type { OrderPrices } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { selectFreeShippingThreshold } from "@jti/store/selectors/selectFreeShippingThreshold";

/**
 * Decides whether the free-shipping progress bar should be rendered.
 *
 * The bar only makes sense when there is a real shipping cost the customer can
 * work toward eliminating, or when they already earned free shipping by
 * reaching the threshold.
 *
 * Rules:
 * - Feature flag must be enabled and user must be logged in.
 * - The effective shipping cost must be a known number. When it is unknown
 *   (null/undefined) — e.g. no shipping cost data available — hide the bar
 *   regardless of any other signal.
 * - Show the bar when there is a non-zero shipping cost to progress against.
 * - Show the bar when the subtotal genuinely reached the threshold, so the
 *   "reached" celebration state can render.
 * - Otherwise hide it (e.g. shipping is already free without reaching the
 *   threshold via a coupon). */

type UseShouldDisplayFreeShippingProgressBarReturn = {
  shouldDisplayFreeShippingProgressBar: boolean;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export const useShouldDisplayFreeShippingProgressBar = (
  prices?: OrderPrices | null
): UseShouldDisplayFreeShippingProgressBarReturn => {
  const { featureFlags } = useConfig();
  const isUserLoggedIn: boolean = useSelector((state) => !state.cart.notLoggedIn);
  const freeShippingThreshold = useSelector(selectFreeShippingThreshold);

  const isFeatureEnabled = Boolean(featureFlags?.ffFreeShippingProgressBar);
  const effectiveShippingValue = prices?.shipping?.value ?? prices?.estimatedShippingCost?.value;

  const isFeatureAvailableForUser = isFeatureEnabled && isUserLoggedIn;

  const hasKnownShippingValue = isFiniteNumber(effectiveShippingValue);
  const subtotalValue = prices?.subtotal?.value;

  const isThresholdReachedBySubtotal =
    isFiniteNumber(subtotalValue) && isFiniteNumber(freeShippingThreshold) && subtotalValue >= freeShippingThreshold;

  const hasShippingCost = hasKnownShippingValue && effectiveShippingValue > 0;

  const shouldDisplayFreeShippingProgressBar =
    isFeatureAvailableForUser && hasKnownShippingValue && (hasShippingCost || isThresholdReachedBySubtotal);

  return { shouldDisplayFreeShippingProgressBar };
};
